import { useEffect, useMemo, useState } from 'react';
import * as React from 'react';

import Button from '@/components/elements/Button/BaseButton';
import ModalBase from '@/components/layouts/Modal/Base';
import ErrorModal from '@/components/layouts/Modal/Error';
import {
  EntryRacer,
  EntryRacerNote,
  useDeleteEntryRacerNoteMutation,
} from '@/lib/apollo/types/gen/graphql';
import { formatRaceInfoText } from '@/lib/race';
import { getVelodromeByCode } from '@/lib/race/velodrome';
import { defaultTheme } from '@/utility/theme';

import Form, { Display } from './Form';
import {
  Buttons,
  Content,
  DeleteButton,
  DeleteTextArea,
  DeleteTitle,
  EditButton,
  EntryRacerName,
  IconArea,
  Note,
  NoteListArea,
  RaceDate,
  RaceInfo,
  Title,
  Wrapper,
} from './style';

interface EntryRacerNoteModalProps {
  isOpen: boolean;
  setIsOpen: (bool: boolean) => void;
  raceId: string | undefined;
  targetEntryRacer: EntryRacer | null;
  entryRacerNoteList: EntryRacerNote[];
  setEntryRacerNoteList: React.Dispatch<React.SetStateAction<EntryRacerNote[]>>;
}

const EntryRacerNoteModal: React.FC<EntryRacerNoteModalProps> = ({
  isOpen,
  setIsOpen,
  raceId,
  targetEntryRacer,
  entryRacerNoteList,
  setEntryRacerNoteList,
}) => {
  if (!raceId || !targetEntryRacer) return <></>;

  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const targetEntryRaceNoteList = useMemo(() => {
    return entryRacerNoteList.filter(
      ernl => ernl.registrationNum === targetEntryRacer.registrationNum,
    );
  }, [targetEntryRacer, entryRacerNoteList]);

  const [deleteEntryRacerNoteMutation, deleteEntryRacerNoteMutationResult] =
    useDeleteEntryRacerNoteMutation();
  const [isOpenErrorModal, setIsOpenErrorModal] = useState<boolean>(false);

  const [display, setDisplay] = useState<Display>(
    targetEntryRaceNoteList.length > 0 ? Display.List : Display.Create,
  );

  const [targetNote, setTargetNote] = useState<EntryRacerNote>(
    {} as EntryRacerNote,
  );

  useEffect(() => {
    if (targetEntryRaceNoteList.length === 0) {
      setDisplay(Display.Create);
    } else {
      setDisplay(Display.List);
    }
  }, [targetEntryRaceNoteList, isOpen]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const isAlreadyExist =
    targetEntryRaceNoteList.findIndex(ern => ern.raceId === raceId) >= 0;

  const changeDisplay = (ern: EntryRacerNote, _display: Display) => {
    setTargetNote(ern);
    setDisplay(_display);
  };

  const deleteEntryRacerNote = () => {
    if (isProcessing) return;
    setIsProcessing(true);
    deleteEntryRacerNoteMutation({
      variables: {
        raceID: targetNote.raceId,
        registrationNum: targetEntryRacer.registrationNum,
      },
    })
      .then(_ => {
        setEntryRacerNoteList(prev => {
          return prev.filter(ern => {
            if (ern.registrationNum === targetEntryRacer.registrationNum) {
              return ern.raceId !== targetNote.raceId;
            }
            return true;
          });
        });
      })
      .catch(() => {
        setIsOpenErrorModal(true);
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  return (
    <ModalBase
      isOpen={isOpen}
      handleClose={handleClose}
      backgroundColor={defaultTheme.background.white}
    >
      <Wrapper>
        <EntryRacerName>{targetEntryRacer.name} 選手メモ</EntryRacerName>
        {targetEntryRaceNoteList.length > 0 && display === Display.List && (
          <>
            <NoteListArea>
              {targetEntryRaceNoteList.map((ern, i) => (
                <React.Fragment key={i}>
                  <Title>
                    <RaceDate>{ern.heldDate.format('YYYY/M/D')}</RaceDate>
                    {getVelodromeByCode(ern.velodromeCode).name}
                    {ern.raceNum}R
                    <RaceInfo>
                      {formatRaceInfoText(
                        ern.race.raceClassName,
                        ern.race.raceEventName,
                      )}
                    </RaceInfo>
                  </Title>
                  <Content>
                    <Note>{ern.body}</Note>
                    <IconArea>
                      <EditButton
                        onClick={() => changeDisplay(ern, Display.Edit)}
                      />
                      <DeleteButton
                        onClick={() => changeDisplay(ern, Display.Delete)}
                      />
                    </IconArea>
                  </Content>
                </React.Fragment>
              ))}
            </NoteListArea>
            {!isAlreadyExist && (
              <Buttons>
                <Button
                  size="m"
                  bgColor="primary"
                  fontWeight="bold"
                  height="56px"
                  radius="l"
                  outline
                  onClick={() => setDisplay(Display.Create)}
                >
                  メモを追加する
                </Button>
              </Buttons>
            )}
          </>
        )}
        {[Display.Create, Display.Edit].includes(display) && (
          <Form
            raceId={raceId}
            registrationNum={targetEntryRacer.registrationNum}
            setEntryRacerNoteList={setEntryRacerNoteList}
            targetNote={targetNote}
            display={display}
          />
        )}
        {targetEntryRaceNoteList.length > 0 &&
          display === Display.Delete &&
          targetNote && (
            <>
              <DeleteTextArea>
                <DeleteTitle>
                  <RaceDate>{targetNote.heldDate.format('YYYY/M/D')}</RaceDate>
                  {getVelodromeByCode(targetNote.velodromeCode).name}
                  {targetNote.raceNum}R
                  <RaceInfo>
                    {formatRaceInfoText(
                      targetNote.race.raceClassName,
                      targetNote.race.raceEventName,
                    )}
                  </RaceInfo>
                </DeleteTitle>
                <p>選手メモを削除しますか？</p>
              </DeleteTextArea>
              <Buttons>
                <Button
                  size="m"
                  bgColor="darkGray"
                  fontWeight="bold"
                  height="56px"
                  radius="l"
                  outline
                  onClick={deleteEntryRacerNote}
                >
                  メモを削除する
                </Button>
              </Buttons>
            </>
          )}
      </Wrapper>
      <ErrorModal
        isOpen={isOpenErrorModal}
        handleClose={() => setIsOpenErrorModal(false)}
        apolloError={deleteEntryRacerNoteMutationResult.error}
      />
    </ModalBase>
  );
};

export default EntryRacerNoteModal;
